import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// In-memory access token — falls back to sessionStorage for the tab lifetime.
// Auth is primarily driven by an httpOnly `uc_session` cookie set by the backend
// (XSS-resistant); the header path here is only kept for tests + non-cookie callers.
let _accessToken = null;
try {
  const cached = typeof sessionStorage !== "undefined" ? sessionStorage.getItem("uc_token") : null;
  if (cached) _accessToken = cached;
} catch (e) {
  console.error("session storage init failed", e);
}

export const setAccessToken = (t) => {
  _accessToken = t || null;
  try {
    if (typeof sessionStorage !== "undefined") {
      if (t) sessionStorage.setItem("uc_token", t);
      else sessionStorage.removeItem("uc_token");
    }
  } catch (e) {
    console.error("session storage write failed", e);
  }
};
export const getAccessToken = () => _accessToken;

export const api = axios.create({ baseURL: API, withCredentials: true });

api.interceptors.request.use((cfg) => {
  if (_accessToken) cfg.headers.Authorization = `Bearer ${_accessToken}`;
  return cfg;
});

api.interceptors.response.use(
  (r) => r,
  (err) => {
    const url = err.config?.url || "";
    const isAuthEndpoint = url.includes("/auth/");
    const isVaultEndpoint = url.includes("/vault/");
    if (err?.response?.status === 401 && !isAuthEndpoint && !isVaultEndpoint) {
      setAccessToken(null);
      // FULFILLMENT TRANSPARENCY — carry the reason so the login page states it clinically
      if (window.location.pathname !== "/login") window.location.href = "/login?expired=1";
    }
    // SOVEREIGN BILLING — tier cap reached: surface an explicit upgrade prompt, never silent
    if (err?.response?.status === 402 && err.response.data?.detail?.error === "upgrade_required") {
      window.dispatchEvent(new CustomEvent("uc:upgrade-required", { detail: err.response.data.detail }));
    }
    return Promise.reject(err);
  }
);

export const authApi = {
  register: (email, password, command_center_name) =>
    api.post("/auth/register", { email, password, command_center_name }).then((r) => r.data),
  login: (email, password) => api.post("/auth/login", { email, password }).then((r) => r.data),
  logout: () => api.post("/auth/logout").then((r) => r.data),
  me: () => api.get("/auth/me").then((r) => r.data),
  updateSettings: (data) => api.put("/settings", data).then((r) => r.data),
};

export const venturesApi = {
  list: () => api.get("/ventures").then((r) => r.data),
  create: (v) => api.post("/ventures", v).then((r) => r.data),
  update: (id, v) => api.put(`/ventures/${id}`, v).then((r) => r.data),
  remove: (id) => api.delete(`/ventures/${id}`).then((r) => r.data),
  pulse: () => api.get("/ventures/pulse").then((r) => r.data),
  refreshMarket: () => api.post("/market/refresh", {}, { timeout: 60000 }).then((r) => r.data),
};

export const agentsApi = {
  run: (agent, focus) => api.post("/agents/run", { agent, focus }).then((r) => r.data),
  list: (agent) => api.get("/agents/insights", { params: agent ? { agent } : {} }).then((r) => r.data),
  act: (id, action) => api.post(`/agents/insights/${id}/action`, { action }).then((r) => r.data),
};

export const paymentsApi = {
  config: () => api.get("/payments/config").then((r) => r.data),
  setupIntent: () => api.post("/payments/setup-intent").then((r) => r.data),
  saveMethod: (stripe_payment_method_id, label) =>
    api.post("/payments/methods", { stripe_payment_method_id, label }).then((r) => r.data),
  listMethods: () => api.get("/payments/methods").then((r) => r.data),
  removeMethod: (id) => api.delete(`/payments/methods/${id}`).then((r) => r.data),
  transactions: () => api.get("/payments/transactions").then((r) => r.data),
  exportCsvUrl: () => `${API}/payments/transactions/export.csv`,
  approveCharge: (insight_id, payment_method_id) =>
    api.post(`/payments/insights/${insight_id}/approve-charge`, { payment_method_id }).then((r) => r.data),
  activateLive: (publishable_key, secret_key) =>
    api.post("/payments/activate-live", { publishable_key, secret_key }, { timeout: 30000 }).then((r) => r.data),
};

export const inboxApi = {
  config: () => api.get("/inbox/config").then((r) => r.data),
  rotateSecret: () => api.post("/inbox/rotate-secret").then((r) => r.data),
  signals: () => api.get("/inbox/signals").then((r) => r.data),
};

export const twilioApi = {
  status: () => api.get("/settings/twilio/status").then((r) => r.data),
  test: () => api.post("/settings/twilio/test").then((r) => r.data),
  deliveries: () => api.get("/settings/sms-deliveries").then((r) => r.data),
  setPhone: (phone_number) => api.put("/settings", { phone_number }).then((r) => r.data),
};

export const actionHubApi = {
  list: () => api.get("/action-hub").then((r) => r.data),
  execute: (kind, id, opts = {}) => api.post(`/action-hub/${kind}/${id}/execute`, {
    kind,
    payment_method_id: opts.payment_method_id || null,
    signature_data_url: opts.signature_data_url || null,
    signature_name: opts.signature_name || null,
  }).then((r) => r.data),
};

export const vaultApi = {
  setPin: (pin) => api.post("/vault/set-pin", { pin }).then((r) => r.data),
  verifyPin: (pin) => api.post("/vault/verify-pin", { pin }).then((r) => r.data),
  list: (vault_token) => api.get("/vault/documents", { params: { vault_token } }).then((r) => r.data),
  upload: (vault_token, doc) =>
    api.post("/vault/documents", doc, { params: { vault_token } }).then((r) => r.data),
  get: (id, vault_token) =>
    api.get(`/vault/documents/${id}`, { params: { vault_token } }).then((r) => r.data),
  remove: (id, vault_token) =>
    api.delete(`/vault/documents/${id}`, { params: { vault_token } }).then((r) => r.data),
  checkin: () => api.post("/vault/checkin").then((r) => r.data),
  status: () => api.get("/vault/dead-man-status").then((r) => r.data),
};

export const alertsApi = {
  list: () => api.get("/alerts").then((r) => r.data),
  create: (data) => api.post("/alerts", data).then((r) => r.data),
  markRead: (id) => api.post(`/alerts/${id}/read`).then((r) => r.data),
};

export const tasksApi = {
  list: (status) => api.get("/tasks", { params: status ? { status_filter: status } : {} }).then((r) => r.data),
  create: (data) => api.post("/tasks", data).then((r) => r.data),
  update: (id, data) => api.put(`/tasks/${id}`, data).then((r) => r.data),
  remove: (id) => api.delete(`/tasks/${id}`).then((r) => r.data),
  readArtifact: (id) => api.get(`/tasks/${id}/artifact`).then((r) => r.data),
  downloadArtifact: (id, format) =>
    api.get(`/tasks/${id}/artifact/download`, { params: { format }, responseType: "blob" }),
  emailArtifact: (id, payload) =>
    api.post(`/tasks/${id}/artifact/email`, payload, { timeout: 45000 }).then((r) => r.data),
};

export const briefingApi = {
  get: () => api.get("/briefing").then((r) => r.data),
};

export const warroomApi = {
  createPlan: (venture_id, situation) => api.post("/warroom/battle-plan", { venture_id, situation }).then((r) => r.data),
  listPlans: () => api.get("/warroom/plans").then((r) => r.data),
  getPlan: (id) => api.get(`/warroom/plans/${id}`).then((r) => r.data),
};

export const auditorApi = {
  rescan: (doc_id, vault_token) => api.post(`/auditor/scan/${doc_id}`, null, { params: { vault_token } }).then((r) => r.data),
  scanInbox30d: () => api.post("/auditor/scan-inbox-30d").then((r) => r.data),
};

export const terminalApi = {
  list: (since) => api.get("/terminal/events", { params: since ? { since, limit: 200 } : { limit: 200 } }).then((r) => r.data),
  emit: (payload) => api.post("/terminal/events", payload).then((r) => r.data),
  feed: (mode) => api.get("/terminal/feed", { params: { mode } }).then((r) => r.data),
  quickReply: (insight_id, command, note = "") =>
    api.post("/terminal/quick-reply", { insight_id, command, note }).then((r) => r.data),
  transcribe: (blob) => {
    const fd = new FormData();
    fd.append("audio", blob, "mandate.webm");
    return api.post("/stt/transcribe", fd, { timeout: 60000 }).then((r) => r.data);
  },
  fileMandate: (text, via = "voice") =>
    api.post("/terminal/mandate", { text, via }, { timeout: 90000 }).then((r) => r.data),
  correctRoute: (task_id, payload) =>
    api.put(`/terminal/mandate/${task_id}/route`, payload).then((r) => r.data),
};

export const unifiedLoopApi = {
  fireFromInsight: (insight_id) => api.post(`/unified-loop/from-insight/${insight_id}`).then((r) => r.data),
};

export const infrastructureApi = {
  landingPage: (payload) => api.post("/agents/creator/landing-page", payload).then((r) => r.data),
};

export const webhookApi = {
  listEvents: (unresolved_only = false) => api.get("/webhook-events", { params: { unresolved_only } }).then((r) => r.data),
  resolve: (id) => api.post(`/webhook-events/${id}/resolve`).then((r) => r.data),
  fixQueue: () => api.get("/fix-priority-queue").then((r) => r.data),
  regenerate: (venture_id) => api.post(`/ventures/${venture_id}/regenerate-webhook`).then((r) => r.data),
};

export const connectivityApi = {
  status: () => api.get("/connectivity/status").then((r) => r.data),
  connectGmail: (email, app_password, imap_host) =>
    api.post("/connectivity/gmail", { email, app_password, imap_host }).then((r) => r.data),
  disconnectGmail: () => api.delete("/connectivity/gmail").then((r) => r.data),
  scanGmail: () => api.post("/connectivity/gmail/scan").then((r) => r.data),
  testSendGmail: () => api.post("/connectivity/gmail/test-send").then((r) => r.data),
};

export const intelligenceApi = {
  scan: (venture_ids, limit_per_venture = 3) =>
    api.post("/intelligence/scan", { venture_ids, limit_per_venture }).then((r) => r.data),
  findings: (limit = 40) => api.get("/intelligence/findings", { params: { limit } }).then((r) => r.data),
};

export const executionApi = {
  runPython: (code, timeout) =>
    api.post("/execution/python", { code, timeout }, { timeout: 90000 }).then((r) => r.data),
  runBrowser: (url, extract_selector, screenshot = true) =>
    api.post("/execution/browser", { url, extract_selector, screenshot }, { timeout: 90000 }).then((r) => r.data),
  recon: (ventureId) =>
    api.post(`/execution/browser-recon/${ventureId}`, {}, { timeout: 120000 }).then((r) => r.data),
  source: (payload) =>
    api.post("/execution/source", payload, { timeout: 150000 }).then((r) => r.data),
  systemCleanup: () =>
    api.post("/executor/system-cleanup", {}, { timeout: 120000 }).then((r) => r.data),
  history: (limit = 20) => api.get(`/execution/history?limit=${limit}`).then((r) => r.data),
};

export const aideApi = {
  chat: (session_id, message) =>
    api.post("/aide/chat", { session_id, message }, { timeout: 60000 }).then((r) => r.data),
};

export const accountApi = {
  exportData: () => api.get("/account/export", { timeout: 60000 }).then((r) => r.data),
  deleteAccount: (confirm_email) =>
    api.delete("/account", { data: { confirm_email } }).then((r) => r.data),
};

export const heatmapApi = {
  get: () => api.get("/heatmap").then((r) => r.data),
};

export const deadManApi = {
  simulate: () => api.post("/dead-man/simulate").then((r) => r.data),
  events: () => api.get("/dead-man-events").then((r) => r.data),
};

export const systemApi = {
  pineconeStatus: () => api.get("/system/pinecone-status").then((r) => r.data),
  smtpTest: () => api.post("/system/smtp-test").then((r) => r.data),
  rotateVaultKey: () => api.post("/vault/rotate-key").then((r) => r.data),
  keyRotations: () => api.get("/vault/key-rotations").then((r) => r.data),
};

export const draftsApi = {
  list: (status) => api.get("/drafts", { params: status ? { status_filter: status } : {} }).then((r) => r.data),
  create: (data) => api.post("/drafts", data).then((r) => r.data),
  update: (id, data) => api.put(`/drafts/${id}`, data).then((r) => r.data),
  remove: (id) => api.delete(`/drafts/${id}`).then((r) => r.data),
  createFromCreator: (venture_id, focus) =>
    api.post(`/agents/creator/draft`, null, { params: { venture_id, focus: focus || "" } }).then((r) => r.data),
  bulkCampaign: (venture_id, theme) =>
    api.post("/agents/creator/bulk-campaign", { venture_id, theme: theme || "" }, { timeout: 120000 }).then((r) => r.data),
};

export const vaultScanApi = {
  scan: (vault_token, name, mime, content_b64, venture_id) =>
    api.post("/vault/scan-document", { name, mime, content_b64, venture_id }, { params: { vault_token } }).then((r) => r.data),
};

export const sweepApi = {
  runNow: () => api.post("/sweep/run-now").then((r) => r.data),
  status: () => api.get("/sweep/status").then((r) => r.data),
};

export const optimizerApi = {
  proposals: () => api.get("/optimizer/proposals").then((r) => r.data),
  signatures: () => api.get("/optimizer/signatures").then((r) => r.data),
};

export const platformApi = {
  config: () => api.get("/platform/config").then((r) => r.data),
  setPublicRollout: (enabled) => api.put("/platform/public-rollout", { enabled }).then((r) => r.data),
};

export const connectApi = {
  status: () => api.get("/connect/status").then((r) => r.data),
  onboardingLink: () => api.post("/connect/onboarding-link", {}, { timeout: 30000 }).then((r) => r.data),
};

export const arbitrageApi = {
  scan: () => api.post("/arbitrage/scan", {}, { timeout: 150000 }).then((r) => r.data),
  tasks: () => api.get("/arbitrage/tasks").then((r) => r.data),
  approve: (id, rfq_id) => api.post(`/arbitrage/tasks/${id}/approve`, rfq_id ? { rfq_id } : {}, { timeout: 150000 }).then((r) => r.data),
  summary: () => api.get("/arbitrage/summary").then((r) => r.data),
  markPaid: (entryId) => api.post(`/arbitrage/ledger/${entryId}/mark-paid`).then((r) => r.data),
};

export const revenueWalletApi = {
  get: () => api.get("/wallet").then((r) => r.data),
  connectBank: (payload) => api.post("/wallet/connect-bank", payload).then((r) => r.data),
  deploy: (amount) => api.post("/wallet/deploy", { amount }, { timeout: 30000 }).then((r) => r.data),
};

export const huddleApi = {
  run: (insight_id) => api.post("/huddle/run", { insight_id }, { timeout: 180000 }).then((r) => r.data),
  pending: () => api.get("/huddle/pending").then((r) => r.data),
  execute: (id) => api.post(`/huddle/${id}/execute`, {}, { timeout: 90000 }).then((r) => r.data),
  overrides: () => api.get("/huddle/overrides").then((r) => r.data),
};

export const laborApi = {
  scoreboard: () => api.get("/labor/scoreboard").then((r) => r.data),
  enforce: () => api.post("/labor/enforce", {}, { timeout: 150000 }).then((r) => r.data),
  digest: () => api.get("/labor/digest").then((r) => r.data),
};

export const growthApi = {
  channelPulse: () => api.get("/creator/channel-pulse").then((r) => r.data),
  brandMaintenance: () => api.post("/creator/brand-maintenance/run", {}, { timeout: 180000 }).then((r) => r.data),
};

export const leadsApi = {
  list: () => api.get("/leads").then((r) => r.data),
  setStatus: (id, status) => api.patch(`/leads/${id}/status`, { status }).then((r) => r.data),
  huddle: (id) => api.post(`/leads/${id}/huddle`, {}, { timeout: 180000 }).then((r) => r.data),
};

export const oracleApi = {
  chat: (message, session_id) => api.post("/oracle/chat", { message, session_id }, { timeout: 90000 }).then((r) => r.data),
  history: (session_id) => api.get("/oracle/history", { params: { session_id } }).then((r) => r.data),
  context: () => api.get("/briefing").then((r) => r.data),
  morningBriefing: () => api.get("/oracle/morning-briefing", { timeout: 60000 }).then((r) => r.data),
  shadowSim: () => api.post("/oracle/shadow-simulation", {}, { timeout: 120000 }).then((r) => r.data),
  latestSession: () => api.get("/oracle/latest-session").then((r) => r.data),
  operatorReset: () => api.post("/oracle/operator-reset", {}, { timeout: 90000 }).then((r) => r.data),
};

export const evolutionApi = {
  status: () => api.get("/evolution/status").then((r) => r.data),
  runNow: () => api.post("/evolution/run-now", {}, { timeout: 180000 }).then((r) => r.data),
};

export const genesisApi = {
  launch: (name, mission) => api.post("/genesis/launch", { name, mission }, { timeout: 120000 }).then((r) => r.data),
  registry: () => api.get("/genesis/registry").then((r) => r.data),
};

export const executionsApi = {
  list: () => api.get("/executions").then((r) => r.data),
  run: (task_id) => api.post("/executions/run", { task_id }, { timeout: 60000 }).then((r) => r.data),
  override: (task_id) => api.post("/executions/override", { task_id }, { timeout: 60000 }).then((r) => r.data),
  traffic: () => api.get("/traffic").then((r) => r.data),
};

export const bridgeApi = {
  handshake: () => api.post("/bridge/handshake", {}, { timeout: 45000 }).then((r) => r.data),
  setTarget: (app, base_url) => api.put("/bridge/targets", { app, base_url }).then((r) => r.data),
  setSecret: (app, secret) => api.put("/bridge/secrets", { app, secret }).then((r) => r.data),
  secrets: () => api.get("/bridge/secrets").then((r) => r.data),
  alignmentKit: () => api.post("/bridge/alignment-kit", {}, { timeout: 45000 }).then((r) => r.data),
};

export const vaultReaderApi = {
  artifacts: (params = {}) => api.get("/vault/artifacts", { params, timeout: 60000 }).then((r) => r.data),
  quicklook: (path) => api.get("/vault/artifacts/quicklook", { params: { path } }).then((r) => r.data),
  brief: (payload) => api.post("/vault/artifacts/brief", payload, { timeout: 90000 }).then((r) => r.data),
  census: () => api.post("/vault/census", {}, { timeout: 180000 }).then((r) => r.data),
  fileContract: (payload) => api.post("/vault/contracts/file", payload, { timeout: 45000 }).then((r) => r.data),
  patches: () => api.get("/patches").then((r) => r.data),
  proposePatch: (agent, capability) => api.post("/patches/propose", { agent, capability }).then((r) => r.data),
  approvePatch: (id) => api.post(`/patches/${id}/approve`, {}, { timeout: 45000 }).then((r) => r.data),
  download: (id, params = {}) => api.get(`/vault/artifacts/${id}/download`, { params, responseType: "blob", timeout: 90000 }),
  retroTag: () => api.post("/vault/retro-tag", {}, { timeout: 120000 }).then((r) => r.data),
  reassign: (ids, venture, scope) => api.put("/vault/artifacts/reassign", { ids, venture, scope: scope || undefined }).then((r) => r.data),
  roiLeaderboard: () => api.get("/vault/roi-leaderboard").then((r) => r.data),
  triageSweep: () => api.post("/vault/triage-sweep", {}, { timeout: 300000 }).then((r) => r.data),
  triageSweepPending: () => api.get("/vault/triage-sweep").then((r) => r.data),
  triageSweepApprove: () => api.post("/vault/triage-sweep/approve", {}, { timeout: 120000 }).then((r) => r.data),
  webhooks: () => api.get("/vault/webhooks").then((r) => r.data),
  handshake: () => api.get("/vault/handshake", { timeout: 45000 }).then((r) => r.data),
  auditTrail: () => api.get("/vault/audit-trail").then((r) => r.data),
};

export const memoryApi = {
  list: (limit = 50) => api.get("/memory", { params: { limit } }).then((r) => r.data),
};

export const creditsApi = {
  ledger: () => api.get("/credits/ledger").then((r) => r.data),
};

export const rfqApi = {
  list: (rfq_status = "pending") =>
    api.get("/rfq", { params: { rfq_status } }).then((r) => r.data),
  get: (id) => api.get(`/rfq/${id}`).then((r) => r.data),
  authorize: (id) => api.post(`/rfq/${id}/authorize`, {}, { timeout: 180000 }).then((r) => r.data),
  negotiate: (id, counter_offer, max_credits) =>
    api.post(`/rfq/${id}/negotiate`, { counter_offer, max_credits }).then((r) => r.data),
  reject: (id) => api.post(`/rfq/${id}/reject`).then((r) => r.data),
};

export const workRateApi = {
  get: () => api.get("/work-rate").then((r) => r.data),
};

export const rfpApi = {
  list: (rfp_status = "pending") =>
    api.get("/rfp", { params: { rfp_status } }).then((r) => r.data),
  sign: (id, signature_name) =>
    api.post(`/rfp/${id}/sign`, { signature_name }, { timeout: 60000 }).then((r) => r.data),
  reject: (id) => api.post(`/rfp/${id}/reject`).then((r) => r.data),
  outcome: (id, bite) => api.post(`/rfp/${id}/outcome`, { bite }).then((r) => r.data),
  winRate: () => api.get("/rfp/win-rate").then((r) => r.data),
};

export const arbSessionApi = {
  list: () => api.get("/arbitrage/sessions").then((r) => r.data),
  store: (platform, cookies) =>
    api.put("/arbitrage/sessions", { platform, cookies }).then((r) => r.data),
  remove: (platform) => api.delete(`/arbitrage/sessions/${platform}`).then((r) => r.data),
  scan: () => api.post("/arbitrage/session-scan", {}, { timeout: 180000 }).then((r) => r.data),
  godaddyRecovery: (venture_name) =>
    api.post("/arbitrage/godaddy-recovery", { venture_name }, { timeout: 120000 }).then((r) => r.data),
};

export const billingApi = {
  tiers: () => api.get("/billing/tiers").then((r) => r.data),
  checkout: (tier, origin_url) =>
    api.post("/billing/checkout", { tier, origin_url }).then((r) => r.data),
  status: (sessionId) => api.get(`/billing/status/${sessionId}`).then((r) => r.data),
  portal: (origin_url) => api.post("/billing/portal", { origin_url }).then((r) => r.data),
  invoices: () => api.get("/billing/invoices").then((r) => r.data),
  evidence: () => api.get("/billing/evidence").then((r) => r.data),
};

export const rootApi = {
  status: () => api.get("/root/status").then((r) => r.data),
  collections: () => api.get("/root/collections").then((r) => r.data),
  collection: (name, page = 1, search = "") =>
    api.get(`/root/collections/${name}`, { params: { page, search } }).then((r) => r.data),
  updateDoc: (name, docId, update) =>
    api.put(`/root/collections/${name}/${docId}`, { update }).then((r) => r.data),
  deleteDoc: (name, docId) =>
    api.delete(`/root/collections/${name}/${docId}`).then((r) => r.data),
  audit: () => api.get("/root/audit").then((r) => r.data),
};

export const jarvisApi = {
  session: (venture_id) => api.get("/jarvis/session", { params: { venture_id } }).then((r) => r.data),
  retrieve: (query, venture_id) => api.post("/jarvis/retrieve", { query, venture_id }).then((r) => r.data),
  episodes: (q) => api.post("/jarvis/episodes/search", q).then((r) => r.data),
  consolidate: () => api.post("/jarvis/consolidate").then((r) => r.data),
  envelopes: (params) => api.get("/jarvis/envelopes", { params }).then((r) => r.data),
  flag: (data) => api.post("/jarvis/flag", data).then((r) => r.data),
  flags: () => api.get("/jarvis/flags").then((r) => r.data),
  tools: () => api.get("/jarvis/tools").then((r) => r.data),
  broadcastDraft: (data) => api.post("/jarvis/broadcast/draft", data, { timeout: 90000 }).then((r) => r.data),
  broadcastApprove: (id, edited_payload) =>
    api.post(`/jarvis/broadcast/${id}/approve`, { edited_payload }).then((r) => r.data),
  broadcasts: () => api.get("/jarvis/broadcast").then((r) => r.data),
  campaignCreate: (data) => api.post("/jarvis/warroom/campaigns", data, { timeout: 90000 }).then((r) => r.data),
  campaignApprove: (id) => api.post(`/jarvis/warroom/campaigns/${id}/approve`).then((r) => r.data),
  campaignResults: (id, data) => api.post(`/jarvis/warroom/campaigns/${id}/results`, data).then((r) => r.data),
  campaigns: () => api.get("/jarvis/warroom/campaigns").then((r) => r.data),
  revenueIntel: () => api.get("/jarvis/revenue-intel").then((r) => r.data),
  proactiveGenerate: () => api.post("/jarvis/proactive/generate").then((r) => r.data),
  proactiveDigest: () => api.get("/jarvis/proactive").then((r) => r.data),
  proactiveRespond: (id, action) =>
    api.post(`/jarvis/proactive/${id}/respond`, { action }).then((r) => r.data),
};

export const voiceApi = {
  status: () => api.get("/voice/status").then((r) => r.data),
  tts: (text, speed) => api.post("/voice/tts", { text, speed }, { timeout: 30000 }).then((r) => r.data),
  command: (transcript) => api.post("/voice/command", { transcript }, { timeout: 30000 }).then((r) => r.data),
  clientLatency: (turnId, data) => api.post(`/voice/turns/${turnId}/client`, data).then((r) => r.data),
  turns: () => api.get("/voice/turns").then((r) => r.data),
  brief: (force) => api.get("/voice/brief", { params: force ? { force: true } : {} }).then((r) => r.data),
  briefAudio: () => api.post("/voice/brief/audio", {}, { timeout: 45000 }).then((r) => r.data),
  briefPlayed: () => api.post("/voice/brief/played").then((r) => r.data),
  stt: (blob, filename = "command.webm") => {
    const fd = new FormData();
    fd.append("audio", blob, filename);
    return api.post("/stt/transcribe", fd, { timeout: 30000 }).then((r) => r.data);
  },
};

export const osApi = {
  healthMap: () => api.get("/os/health-map").then((r) => r.data),
  consensus: (status) => api.get("/os/consensus", { params: status ? { status } : {} }).then((r) => r.data),
  act: (id, action, extra = {}) => api.post(`/os/consensus/${id}/act`, { action, ...extra }).then((r) => r.data),
  runCycle: () => api.post("/os/cycle/run", null, { timeout: 60000 }).then((r) => r.data),
  briefing: () => api.get("/os/briefing").then((r) => r.data),
  briefingRegen: () => api.post("/os/briefing", null, { timeout: 60000 }).then((r) => r.data),
  observability: () => api.get("/os/observability").then((r) => r.data),
  budgets: () => api.get("/os/budgets").then((r) => r.data),
};
